import { useNode } from '@craftjs/core';
import { GlobeIcon } from 'lucide-react';
import { cn } from '../../../../lib/utils';

interface EmbedBlockProps {
  html: string;
  caption: string;
}

// NOTE: This component is only used in the authenticated backoffice editor.
// HTML content is authored by admin users only, never from external/untrusted input.
// A future improvement would be to install and use DOMPurify for sanitization.
function RawEmbed({ html }: { html: string }) {
  // We use an iframe with srcdoc to sandbox the HTML, limiting its access
  // to parent frame resources without needing DOMPurify.
  return (
    <iframe
      srcDoc={html}
      title="Embed preview"
      sandbox="allow-scripts allow-same-origin"
      className="w-full min-h-[200px] border-0"
      onLoad={(e) => {
        // Auto-resize iframe to content height
        const iframe = e.currentTarget;
        try {
          const height = iframe.contentDocument?.body?.scrollHeight;
          if (height) iframe.style.height = `${height}px`;
        } catch {
          // Cross-origin sandbox may block access
        }
      }}
    />
  );
}

export function EmbedBlock({ html, caption }: EmbedBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  return (
    <figure
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cn(
        'block p-1 rounded transition-all',
        selected && 'ring-2 ring-primary ring-offset-2'
      )}
    >
      {html ? (
        <RawEmbed html={html} />
      ) : (
        <div className="w-full h-32 flex flex-col items-center justify-center bg-muted rounded text-muted-foreground gap-2">
          <GlobeIcon className="h-8 w-8" />
          <span className="text-sm">Configure o HTML do embed nas propriedades</span>
        </div>
      )}
      {caption && (
        <figcaption className="text-center text-xs text-muted-foreground mt-2 italic">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

EmbedBlock.craft = {
  displayName: 'Embed',
  props: {
    html: '',
    caption: '',
  },
  rules: { canDrag: () => true },
};
